<template>
    <div class="">
        <div class="pb-4 pt-2 pr-4 pl-2 border-b border-neutral-base flex justify-between">
            <DButton
                theme="transparent"
                right-icon="ph:caret-down"
                @click="openCalendars"
            >
                <div v-if="activeCalendar?.avatar">
                    <UIAvatar
                        :avatar="activeCalendar.avatar"
                        size-override="small"
                    />
                </div>
                <div v-if="activeCalendar?.icon">
                    <DAvatar
                        type="icon"
                        :icon="`ph:${activeCalendar.icon.icon}`"
                        size="8"
                    />
                </div>
                <div class="ml-1">
                    {{ activeCalendar?.short_title || activeCalendar?.title || 'Calendar' }}
                </div>
            </DButton>
            <DButton
                theme="secondary"
                :left-icon="sources.length ? 'ph:sliders-horizontal' : 'ph:calendar-dots'"
                :disabled="fetching"
                @click="openSources"
            >
                {{ sources.length ? 'Filter' : 'View' }} <DBadge
                    v-if="filterCount"
                    type="base-invert"
                >
                    {{ filterCount }}
                </DBadge>
            </DButton>
        </div>

        <div class="w-full h-3 surface-secondary" />

        <div>
            <template v-if="selectedView === VIEW_MONTH">
                <div class="py-1">
                    <DDatepicker
                        v-model="selectedDate"
                        :events="eventIndicators"
                        min-date="1970-01-01"
                        max-date="2037-01-01"
                        inline
                        fullwidth
                        :display-dropdowns="false"
                        :locale="schoolLocale"
                        @change-month="onMonthChange"
                        @update:model-value="onDatePicker"
                    />
                </div>

                <div class="w-full h-1 surface-secondary border-t border-neutral-subtle" />

                <div>
                    <div
                        v-if="selectedEvents.length > 0 && !fetching"
                        class="pb-(--beyond-safe-area-inset-bottom)"
                    >
                        <div
                            v-for="(eventGroup, key) in selectedEventsWithHeadings"
                            :key="key"
                        >
                            <p class="text-gray-800 pb-3 pt-5 px-4 surface-secondary font-medium">
                                {{ key }}
                            </p>

                            <ListItem
                                v-for="(event, index) in eventGroup"
                                :key="index"
                                :config="event"
                            />
                        </div>
                    </div>

                    <CalendarEmpty
                        v-else-if="!fetching"
                        :filter-count="filterCount"
                        :clear-filters="clearFilters"
                        subtitle="Please check another day or come back later."
                    />

                    <div
                        v-else
                        class="flex justify-center py-12"
                    >
                        <UILoading class="w-12 h-12" />
                    </div>
                </div>
            </template>
            <template v-if="selectedView === VIEW_WEEK">
                <div class="p-2 py-3 flex items-center justify-between">
                    <DButton
                        theme="transparent"
                        icon="chevron-left-20-solid"
                        @click="addToDate(-1, 'week')"
                    />

                    <span class="text-gray-900 font-medium">
                        {{ weekDisplay }}
                    </span>

                    <DButton
                        theme="transparent"
                        icon="chevron-right-20-solid"
                        @click="addToDate(1, 'week')"
                    />
                </div>

                <div>
                    <div
                        v-if="eventItems.length > 0 && !fetching"
                        class="pb-(--beyond-safe-area-inset-bottom)"
                    >
                        <div
                            v-for="(eventGroup, key) in eventsWithHeadings"
                            :key="key"
                        >
                            <div class="w-full h-2 surface-secondary border-t border-neutral-subtle" />
                            <p class="text-gray-800 py-3 px-4 font-medium border-b border-neutral-base">
                                {{ key }}
                            </p>

                            <ListItem
                                v-for="(event, index) in eventGroup"
                                :key="index"
                                :config="event"
                            />
                        </div>
                    </div>

                    <CalendarEmpty
                        v-else-if="!fetching"
                        :filter-count="filterCount"
                        :clear-filters="clearFilters"
                        subtitle="Please check another week or come back later."
                    />

                    <div
                        v-else
                        class="flex justify-center py-12"
                    >
                        <UILoading class="w-12 h-12" />
                    </div>
                </div>
            </template>
            <template v-if="selectedView === VIEW_DAY">
                <div class="p-3 flex items-center justify-between border-b border-neutral-subtle">
                    <DButton
                        theme="transparent"
                        icon="chevron-left-20-solid"
                        @click="addToDate(-1, 'day')"
                    />

                    <span class="text-gray-900 font-medium">
                        <span :class="{ 'text-(--color-brand-700) font-semibold': dayNameDisplay === 'Today' }">{{ dayNameDisplay }}</span>, {{ dayDisplay }}
                    </span>

                    <DButton
                        theme="transparent"
                        icon="chevron-right-20-solid"
                        @click="addToDate(1, 'day')"
                    />
                </div>

                <div class="w-full h-1 surface-secondary" />

                <div>
                    <div
                        v-if="eventItems.length > 0 && !fetching"
                        class="pb-(--beyond-safe-area-inset-bottom)"
                    >
                        <ListItem
                            v-for="(event, index) in eventItems"
                            :key="index"
                            :config="event"
                        />
                    </div>

                    <CalendarEmpty
                        v-else-if="!fetching"
                        :filter-count="filterCount"
                        :clear-filters="clearFilters"
                        subtitle="Please check another day or come back later."
                    />

                    <div
                        v-else
                        class="flex justify-center py-12"
                    >
                        <UILoading class="w-12 h-12" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
    import {
        DAvatar,
        DBadge,
        DButton, DDatepicker,
    } from '@digistorm/spark'
    import {
        map, filter, groupBy, isObject, isEmpty,
    } from 'lodash'
    import { isCancel } from 'axios'
    import { modalController } from '@ionic/vue'
    import { preferences } from '@/services'
    import dayjs from '@/dayjs'
    import CalendarSourcesModal from '@/components/filters/CalendarSourcesModal.vue'
    import CalendarTypesModal from '@/components/filters/CalendarTypesModal.vue'
    import UILoading from '@/components/ui/UILoading.vue'
    import ListItem from '@/components/modules/ListItem.vue'
    import { disableAutomaticRightIconKey } from '@/keys'
    import UIAvatar from '@/components/ui/UIAvatar.vue'
    import CalendarEmpty from '@/components/app/CalendarEmpty.vue'
    import { useDateHelper } from '@/composables/useDateHelper'

    const props = defineProps({
        config: Object,
    })

    const calendarHelpers = useCalendarHelpers()
    const moduleHelpers = useModuleHelpers()
    const moduleStore = useModuleStore()

    provide(disableAutomaticRightIconKey, true)

    const {
        VIEW_MONTH,
        VIEW_WEEK,
        VIEW_DAY,
        generateMultiDayEventsAsItems,
        getStartForView,
        getEndForView,
        getDateForNewMonth,
        getSchoolLocale,
    } = calendarHelpers

    const moduleId = inject('moduleId', ref(null))

    const dateHelper = useDateHelper()

    const { loadingId } = storeToRefs(moduleStore)

    const configDate = props.config.data.selected_date
    const now = configDate ? dayjs(configDate) : dayjs()
    const schoolLocale = getSchoolLocale() // This isn't expected to change during use
    const selectedDate = ref(now.format('YYYY-MM-DD'))
    const previouslyLoadedMonth = ref(now.month())
    const selectedView = ref(VIEW_DAY)
    const supportedViews = [VIEW_DAY, VIEW_WEEK, VIEW_MONTH]
    let rangeStart = now.startOf('month').startOf('week')
    let rangeEnd = now.endOf('month').endOf('week')
    const calendarType = ref(null)
    const calendarId = ref(null)
    const filterParams = ref({})

    const shouldSaveFilters = computed(() => !!props.config.meta.id && !!calendarType.value)

    const uniqueKeySuffix = computed(() => {
        if (!shouldSaveFilters.value) {
            return null
        }
        let key = `${props.config.meta.id}-${calendarType.value}`

        // Include calendar ID if type uses it
        if (calendarId.value) {
            key += `-${calendarId.value}`
        }
        return key
    })

    const preferenceKeyForFilters = computed(
        () => (shouldSaveFilters.value ? `calendar-filters-${uniqueKeySuffix.value}` : null),
    )

    const preferenceKeyForView = computed(
        () => (shouldSaveFilters.value ? `calendar-view-${uniqueKeySuffix.value}` : null),
    )

    const fetching = computed(() => loadingId.value === moduleId.value)

    const sources = computed(() => props.config.meta.sources ?? [])

    const activeCalendar = computed(() => filter(props.config.meta.calendars, (calendar) => calendar.active)?.[0])

    const eventItems = computed(() => {
        const calendarEvents = props.config.data.events
        return generateMultiDayEventsAsItems(now.value, calendarEvents, rangeStart, rangeEnd)
    })

    const eventsWithHeadings = computed(() => {
        return groupBy(eventItems.value, 'heading')
    })

    const selectedEvents = computed(() => {
        return filter(eventItems.value, (event) => {
            return event.date === dayjs(selectedDate.value).format('YYYY-MM-DD')
        })
    })

    const selectedEventsWithHeadings = computed(() => {
        return groupBy(selectedEvents.value, 'heading')
    })

    const eventIndicators = computed(() => {
        return map(eventItems.value, 'date')
    })

    const weekDisplay = computed(() => {
        return `Week of ${dateHelper.value.short().formatDate(getStartForView(selectedView.value, selectedDate.value), false)}`
    })

    const dayNameDisplay = computed(() => {
        if (dayjs(selectedDate.value).isToday()) {
            return 'Today'
        }
        return dayjs(selectedDate.value).format('dddd')
    })

    const dayDisplay = computed(() => {
        return dateHelper.value.short().formatDate(dayjs(selectedDate.value), false)
    })

    const filterCount = computed(() => {
        let count = 0
        forEach(sources.value, (source) => {
            let sourceCount = 0
            forEach(source.filters, (filter) => {
                if (filterParams.value?.[source.filters_param]?.includes(filter.id)) {
                    sourceCount++
                }
            })
            // If no filters, but "All" is selected, count as 1
            if (!sourceCount && filterParams.value?.sources?.includes(source.id)) {
                sourceCount = 1
            }
            count += sourceCount
        })
        return count
    })

    const updateRequest = () => {
        rangeStart = getStartForView(selectedView.value, selectedDate.value)
        rangeEnd = getEndForView(selectedView.value, selectedDate.value)

        const params = {
            start: rangeStart.toISOString(),
            end: rangeEnd.toISOString(),
            ...filterParams.value,
        }

        if (calendarType.value) {
            params.calendar = calendarType.value
        }

        if (calendarId.value) {
            params.id = calendarId.value
        }

        moduleStore.setQueryParams(moduleId.value, params)

        moduleStore.loadModule(moduleId.value)
            .catch((error) => {
                if (!isCancel(error)) {
                    throw error
                }
            })
    }

    const applySelectedFiltersFromPreferences = async () => {
        const rawPreferencesValue = await preferences.getItem(preferenceKeyForFilters.value)

        try {
            const filtersFromPreferences = JSON.parse(rawPreferencesValue ?? '{}')
            if (!isObject(filtersFromPreferences)) {
                throw new Error('Filters from preferences is not an object')
            }
            filterParams.value = filtersFromPreferences
        } catch (e) {
            // If we can't parse the filters, clear them to prevent future issues and just return
            await preferences.removeItem(preferenceKeyForFilters.value)
            filterParams.value = {}
        }
    }

    const applyUserViewFromPreferences = async () => {
        const view = await preferences.getItem(preferenceKeyForView.value)
        const viewsSupportedByCalendar = activeCalendar.value?.supported_views ?? supportedViews
        if (viewsSupportedByCalendar.includes(view)) {
            // Only apply view if valid
            selectedView.value = view
        }
    }

    const addToDate = (amount, type) => {
        selectedDate.value = dayjs(selectedDate.value).add(amount, type).format('YYYY-MM-DD')
        updateRequest()
    }

    function onMonthChange({ month, year } = { month: null, year: null }) {
        selectedDate.value = getDateForNewMonth(selectedDate.value, month, year)
        previouslyLoadedMonth.value = dayjs(selectedDate.value).month()
        updateRequest()
    }

    function onDatePicker(newValue) {
        const newMonth = dayjs(newValue).month()
        if (newMonth !== previouslyLoadedMonth.value && selectedView.value === VIEW_MONTH) {
            previouslyLoadedMonth.value = newMonth
            updateRequest()
        }
    }

    const openCalendars = async () => {
        const size = props.config.meta.calendars.length > 3 ? 1 : 0.7
        const modal = await modalController.create({
            cssClass: 'ds-modal-bottom-sheet',
            component: CalendarTypesModal,
            componentProps: {
                calendars: props.config.meta.calendars,
                onSelected: async (selectedCalendar) => {
                    calendarType.value = selectedCalendar.type
                    calendarId.value = selectedCalendar.id
                    // Reset filters when changing calendar
                    filterParams.value = {}
                    // Set active calendar
                    props.config.meta.calendars.forEach((calendar) => {
                        calendar.active = calendar.type === selectedCalendar.type && calendar.id === selectedCalendar.id
                    })

                    // Load preferences for the newly selected calendar
                    if (shouldSaveFilters.value) {
                        await applyUserViewFromPreferences()
                        await applySelectedFiltersFromPreferences()
                    }

                    // Reset view if unsupported
                    if (!(activeCalendar.value?.supported_views || []).includes(selectedView.value)) {
                        selectedView.value = activeCalendar.value.supported_views[0]
                    }

                    updateRequest()
                    modal.dismiss()
                },
                onCancelled: () => {
                    modal.dismiss()
                },
            },
            initialBreakpoint: size,
            breakpoints: [0, size],
        })

        await modal.present()
    }

    const openSources = async () => {
        const modal = await modalController.create({
            cssClass: 'ds-modal-fullscreen',
            component: CalendarSourcesModal,
            componentProps: {
                title: 'Filter',
                sources: sources.value,
                currentView: selectedView.value,
                supportedViews: activeCalendar.value?.supported_views,
                currentParams: filterParams.value,
                applyChanges: async (view, params) => {
                    selectedView.value = view
                    filterParams.value = params

                    if (shouldSaveFilters.value) {
                        // Save view preference
                        await preferences.setItem(preferenceKeyForView.value, view)

                        if (filterCount.value === 0) {
                            // If no filters are selected, remove from preferences
                            await preferences.removeItem(preferenceKeyForFilters.value)
                        } else {
                            // Save filters to preferences
                            await preferences.setItem(preferenceKeyForFilters.value, JSON.stringify(params))
                        }
                    }

                    updateRequest()
                },
            },
        })

        await modal.present()
    }

    const clearFilters = async () => {
        filterParams.value = {}
        // Clear filters from preferences (only if saving filters is enabled)
        if (shouldSaveFilters.value) {
            await preferences.removeItem(preferenceKeyForFilters.value)
        }
        updateRequest()
    }

    onMounted(async () => {
        // Set calendar type from active calendar in config, if provided
        if (props.config.meta.calendars) {
            const activeCalendarInConfig = filter(props.config.meta.calendars, (calendar) => calendar.active)?.[0]
            if (activeCalendarInConfig) {
                calendarType.value = activeCalendarInConfig.type
                calendarId.value = activeCalendarInConfig.id
            }
        }

        dayjs.locale(schoolLocale)
        moduleHelpers.setAnalyticsScreenFromConfig(props.config)

        // Apply filters and view from preferences
        let filtersChanged = false
        let viewChanged = false
        if (shouldSaveFilters.value) {
            const previousView = selectedView.value
            await applyUserViewFromPreferences()
            viewChanged = previousView !== selectedView.value

            await applySelectedFiltersFromPreferences()
            filtersChanged = !isEmpty(filterParams.value)
        }

        // If view/filters have changed, or events aren't provided with the calendar, then load events
        if (viewChanged || filtersChanged || props.config.data.events === null) {
            updateRequest()
        }
    })
</script>
